import { useEffect } from 'react';

/**
 * Per-page <title> + Open Graph / Twitter meta, without a dependency.
 *
 * The site-wide defaults live in public/index.html. A page can call this hook
 * to override the title, description, share image and canonical URL for the
 * duration it is mounted; on unmount the previous values are restored so other
 * pages keep the defaults.
 *
 * Ariv renders in the browser, so a social crawler that runs no JavaScript
 * (WhatsApp, Facebook, iMessage) will only see whatever is in the initial HTML.
 * The build-time prerender (frontend/scripts/prerender.js) closes that gap for
 * every public route: it renders each page after this hook has run and writes
 * the resulting HTML, these tags baked in, into the build. On routes it does
 * not cover — a property page, for instance — this hook still fixes the
 * browser tab title and works for any crawler that runs JavaScript.
 */
const SITE_TITLE = 'Ariv — Start and Run a UK Short Let, All in One App';

function findOrCreate(kindAttr, key) {
  let el = document.head.querySelector(`meta[${kindAttr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(kindAttr, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(kindAttr, key, content) {
  findOrCreate(kindAttr, key).setAttribute('content', content);
}

function getMeta(kindAttr, key) {
  const el = document.head.querySelector(`meta[${kindAttr}="${key}"]`);
  return el ? el.getAttribute('content') : null;
}

function removeMeta(kindAttr, key) {
  const el = document.head.querySelector(`meta[${kindAttr}="${key}"]`);
  if (el) el.remove();
}

// Open Graph tags use property="", Twitter and description use name=".
const OG = 'property';
const NM = 'name';

export default function usePageMeta({ title, description, image, imageAlt, url, robots, type = 'website' } = {}) {
  useEffect(() => {
    // Snapshot the current head so we can restore it on unmount. Captured
    // after any previous cleanup has run, so it holds the site defaults.
    const prev = {
      title: document.title,
      ogTitle: getMeta(OG, 'og:title'),
      ogDesc: getMeta(OG, 'og:description'),
      ogImage: getMeta(OG, 'og:image'),
      ogImageAlt: getMeta(OG, 'og:image:alt'),
      ogImageW: getMeta(OG, 'og:image:width'),
      ogImageH: getMeta(OG, 'og:image:height'),
      ogUrl: getMeta(OG, 'og:url'),
      ogType: getMeta(OG, 'og:type'),
      desc: getMeta(NM, 'description'),
      twTitle: getMeta(NM, 'twitter:title'),
      twDesc: getMeta(NM, 'twitter:description'),
      twImage: getMeta(NM, 'twitter:image'),
      robots: getMeta(NM, 'robots'),
      canonical: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? null,
    };

    if (title) {
      document.title = title;
      setMeta(OG, 'og:title', title);
      setMeta(NM, 'twitter:title', title);
    }
    if (description) {
      setMeta(NM, 'description', description);
      setMeta(OG, 'og:description', description);
      setMeta(NM, 'twitter:description', description);
    }
    if (image) {
      setMeta(OG, 'og:image', image);
      setMeta(NM, 'twitter:image', image);
      if (imageAlt) setMeta(OG, 'og:image:alt', imageAlt);
      // The default share image is a 512x512 square icon. A property photo is
      // a different shape and size, so drop the square hints and let the
      // crawler size the real image itself.
      removeMeta(OG, 'og:image:width');
      removeMeta(OG, 'og:image:height');
    }
    if (url) {
      setMeta(OG, 'og:url', url);
      const canon = document.head.querySelector('link[rel="canonical"]');
      if (canon) canon.setAttribute('href', url);
    }
    if (type) setMeta(OG, 'og:type', type);
    // A live listing is public and says so. build/app.html — served for every
    // path without its own prerendered file, property pages included — is
    // marked noindex so that URLs which do not exist are not invited into an
    // index. Without this, the listings themselves would inherit that and
    // silently drop out of search. useCrawlerNotFound still sets noindex when
    // the listing is genuinely missing.
    setMeta(NM, 'robots', robots);

    return () => {
      document.title = prev.title || SITE_TITLE;
      const restore = (kind, key, val) => { if (val != null) setMeta(kind, key, val); };
      restore(OG, 'og:title', prev.ogTitle);
      restore(OG, 'og:description', prev.ogDesc);
      restore(OG, 'og:image', prev.ogImage);
      restore(OG, 'og:image:alt', prev.ogImageAlt);
      restore(OG, 'og:image:width', prev.ogImageW);
      restore(OG, 'og:image:height', prev.ogImageH);
      restore(OG, 'og:url', prev.ogUrl);
      restore(OG, 'og:type', prev.ogType);
      restore(NM, 'description', prev.desc);
      restore(NM, 'twitter:title', prev.twTitle);
      restore(NM, 'twitter:description', prev.twDesc);
      restore(NM, 'twitter:image', prev.twImage);
      restore(NM, 'robots', prev.robots);
      const canon = document.head.querySelector('link[rel="canonical"]');
      if (canon && prev.canonical != null) canon.setAttribute('href', prev.canonical);
    };
  }, [title, description, image, imageAlt, url, robots, type]);
}
